///
/// EarthBrowser
///
/// Created by Matt Giger
/// Copyright (c) 2025 Geodata Labs
///

import { type EarthCameraState, type GroundDragAnchor } from "../EarthCameraState"
import { wgs } from "@/earthbrowser/utils/geographic"
import type { EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { ZoomGestureRecognizer, type ZoomGestureEventData } from "@stovokor/gestures/ZoomGestureRecognizer"
import { GestureState } from "@stovokor/gestures/GestureRecognizer"

const cZoomSpringDamping = 15

/**
 * EarthZoomHandler - wheel / trackpad zoom for the Earth camera.
 *
 * Zooms about the cursor: the ground under it when the wheel burst began
 * stays where it is on screen (EarthCameraState.zoomAboutScreenPoint), so
 * zooming in lands on what you pointed at instead of the screen centre.
 * Targets go through the zoom spring. With the cursor in space (no ground
 * to hold) the zoom is about the view axis as it always was.
 *
 * Two-finger touch (pinch, twist, pan, tilt swipe) is EarthTwoFingerHandler's;
 * the recognizer's pinch side is switched off here.
 */
export class EarthZoomHandler extends ZoomGestureRecognizer {
	private cameraState: EarthCameraState
	protected context: EarthBrowserContext

	// Ground under the cursor at gesture start (undefined: in space)
	private groundAnchor?: GroundDragAnchor

	constructor(context: EarthBrowserContext, cameraState: EarthCameraState) {
		super({
			pinchEnabled: false,
			wheelSensitivity: 0.001,
			wheelEndDelay: 150
		})
		this.cameraState = cameraState
		this.context = context
		this.priority = 100

		this.onGesture((data: ZoomGestureEventData) => {
			switch (data.state) {
				case GestureState.Began:
					this.startZoom(data)
					break
				case GestureState.Changed:
					this.applyWheelZoom(data)
					break
				case GestureState.Ended:
					this.endZoom()
					break
			}
		})
	}

	private startZoom(data: ZoomGestureEventData): void {
		// Anchor once per gesture (a wheel burst ends 150 ms after its last
		// tick) rather than re-anchoring on the spring-lagged pose each tick,
		// which would lock in the drift of the mid-flight interpolation
		const anchor = this.cameraState.captureGroundDragAnchor(data.x, data.y)
		this.groundAnchor = anchor?.hit ? anchor : undefined
	}

	private endZoom(): void {
		this.groundAnchor = undefined
		this.cameraState.saveCameraState()
	}

	private applyWheelZoom(data: ZoomGestureEventData): void {
		const multiplier = data.isTrackpadPinch ? 200 : 1

		// Each tick steps from the spring's current radius, as the view-ray
		// zoom always has: during a burst the spring's lag absorbs most of
		// each tick, and the wheel rate was tuned with that in place (summing
		// the ticks into a running target zooms several times faster). The
		// ground under the cursor is still held against the gesture-start
		// anchor, so the lag can't accumulate into drift
		const current = this.cameraState.getCurrentParams()
		const delta = -(data.wheelDeltaY ?? 0) * (current.radius - wgs.a) * 0.014 * multiplier

		if (this.groundAnchor) {
			const radius = current.radius - delta * Math.cos(current.tilt)
			const target = this.cameraState.zoomAboutScreenPoint(this.groundAnchor, data.x, data.y, radius)
			if (target) {
				this.cameraState.animateTo(target, cZoomSpringDamping, "user")
				return
			}
		}

		// Cursor in space: zoom along the view axis (handles orientation reset)
		this.cameraState.moveForward(delta, "user")
	}

	override reset(): void {
		super.reset()
		this.groundAnchor = undefined
	}
}
